import type { ModalAddServiceProps } from "../core/types/tsx/components/modalAddService.types";
import { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash2, Check, Upload, ChevronDown } from "lucide-react";
import { AlertModal, type AlertVariant } from "./modals/AlertModal";
import { ConfirmModal, type ConfirmVariant } from "./modals/ConfirmModal";
import { WizardStepper } from "./wizard/WizardStepper";
import { getImageUri } from "../core/utils/imageLoad";

const steps = [1, 2, 3];

const normalizeLookupKey = (value: string) =>
	value
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.replace(/[^a-z0-9\s]/g, "")
		.replace(/\s+/g, " ")
		.trim();

export function ModalAddService({
	isOpen,
	onClose,
	onSubmit,
	mode = "create",
	categories,
	initialData,
}: ModalAddServiceProps) {
	const initialForm = {
		category: "",
		name: "",
		price: "",
		warrantyDays: "",
		estimatedTimeHours: "",
		description: "",
	};

	type FormKeys = keyof typeof initialForm;

	type AlertState = {
		open: boolean;
		title: string;
		message: string;
		variant: AlertVariant;
		confirmText?: string;
	};

	type AlertPayload = {
		title: string;
		message: string;
		variant?: AlertVariant;
		confirmText?: string;
	};

	type ConfirmState = {
		open: boolean;
		title: string;
		message: string;
		confirmLabel: string;
		cancelLabel: string;
		variant: ConfirmVariant;
		onConfirm: () => Promise<void> | void;
	};

	type ConfirmPayload = {
		title: string;
		message: string;
		confirmLabel?: string;
		cancelLabel?: string;
		variant?: ConfirmVariant;
		onConfirm: () => Promise<void> | void;
	};

	const [currentStep, setCurrentStep] = useState(1);
	const [form, setForm] = useState(initialForm);
	const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
	const [openSubcategoryDropdown, setOpenSubcategoryDropdown] = useState(false);
	const [formErrors, setFormErrors] = useState<Record<string, string>>({});
	const [includes, setIncludes] = useState<string[]>([]);
	const [excludes, setExcludes] = useState<string[]>([]);
	const [includeInput, setIncludeInput] = useState("");
	const [excludeInput, setExcludeInput] = useState("");
	const [includeError, setIncludeError] = useState("");
	const [photoError, setPhotoError] = useState("");
	const [photos, setPhotos] = useState<File[]>([]);
	const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
	const [photoPreviewUrls, setPhotoPreviewUrls] = useState<string[]>([]);
	const baseAlertState: AlertState = {
		open: false,
		title: "",
		message: "",
		variant: "info",
	};
	const baseConfirmState: ConfirmState = {
		open: false,
		title: "",
		message: "",
		confirmLabel: "Confirmar",
		cancelLabel: "Cancelar",
		variant: "primary",
		onConfirm: async () => undefined,
	};
	const [alertState, setAlertState] = useState<AlertState>({
		...baseAlertState,
	});
	const [confirmState, setConfirmState] = useState<ConfirmState>({
		...baseConfirmState,
	});
	const [shouldCloseOnAlertDismiss, setShouldCloseOnAlertDismiss] = useState(false);
	const subcategoryDisabled = !form.category;
	const isEditMode = mode === "edit";

	const availableCategories = useMemo(
		() => (categories && categories.length > 0 ? categories : []),
		[categories],
	);

	const selectedCategoryMeta =
		availableCategories.find((category) => category.label === form.category) ??
		availableCategories.find(
			(category) =>
				normalizeLookupKey(category.label) === normalizeLookupKey(form.category),
		);
	const availableSubcategories = selectedCategoryMeta?.subcategories ?? [];
	const selectedSubcategoryLabel =
		selectedSubcategories.length === 0
			? form.category
				? "Selecciona una subcategoría"
				: "Elige una categoría primero"
			: selectedSubcategories[0];

	useEffect(() => {
		if (!isOpen) return;

		const normalizedInitialSubcategories = (initialData?.subcategory ?? [])
			.map((subcategory) => subcategory?.trim())
			.filter((subcategory): subcategory is string => Boolean(subcategory));

		const initialCategory = (initialData?.category ?? "").trim();
		const resolvedCategory =
			availableCategories.find(
				(category) => category.id === initialData?.categoryId,
			)?.label ??
			availableCategories.find(
				(category) =>
					normalizeLookupKey(category.label) ===
					normalizeLookupKey(initialCategory),
			)?.label ??
			initialCategory;

		const resolvedCategorySubcategories =
			availableCategories.find((category) => category.label === resolvedCategory)
				?.subcategories ?? [];

		const firstInitialSubcategory = normalizedInitialSubcategories[0];
		const resolvedInitialSubcategory = firstInitialSubcategory
			? (resolvedCategorySubcategories.find(
					(subcategory) =>
						normalizeLookupKey(subcategory) ===
						normalizeLookupKey(firstInitialSubcategory),
				) ?? firstInitialSubcategory)
			: "";

		const nextForm = {
			category: resolvedCategory,
			name: initialData?.name ?? "",
			price: initialData?.price ?? "",
			warrantyDays: initialData?.warrantyDays ?? "",
			estimatedTimeHours: initialData?.estimatedTimeHours ?? "",
			description: initialData?.description ?? "",
		};

		setCurrentStep(1);
		setForm(nextForm);
		setSelectedSubcategories(
			resolvedInitialSubcategory ? [resolvedInitialSubcategory] : [],
		);
		setOpenSubcategoryDropdown(false);
		setFormErrors({});
		setIncludes(initialData?.includes ?? []);
		setExcludes(initialData?.excludes ?? []);
		setIncludeInput("");
		setExcludeInput("");
		setIncludeError("");
		setPhotoError("");
		setPhotos([]);
		setExistingPhotos(initialData?.existingPhotos ?? []);
		setAlertState({
			open: false,
			title: "",
			message: "",
			variant: "info",
		});
		setConfirmState({
			open: false,
			title: "",
			message: "",
			confirmLabel: "Confirmar",
			cancelLabel: "Cancelar",
			variant: "primary",
			onConfirm: async () => undefined,
		});
		setShouldCloseOnAlertDismiss(false);
	}, [isOpen, initialData, availableCategories]);

	useEffect(() => {
		const nextUrls = photos.map((file) => URL.createObjectURL(file));
		setPhotoPreviewUrls(nextUrls);

		return () => {
			nextUrls.forEach((url) => URL.revokeObjectURL(url));
		};
	}, [photos]);

	useEffect(() => {
		if (!isOpen) return;
		const previousBodyOverflow = document.body.style.overflow;
		const previousHtmlOverflow = document.documentElement.style.overflow;
		document.body.style.overflow = "hidden";
		document.documentElement.style.overflow = "hidden";

		return () => {
			document.body.style.overflow = previousBodyOverflow;
			document.documentElement.style.overflow = previousHtmlOverflow;
		};
	}, [isOpen]);

	const clearError = (field: string) => {
		setFormErrors((prev) => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	const updateForm = (field: FormKeys, value: string) => {
		setForm((prev) => ({ ...prev, [field]: value }));
		clearError(field);
	};

	const selectSubcategory = (sub: string) => {
		clearError("subcategory");
		setSelectedSubcategories([sub]);
		setOpenSubcategoryDropdown(false);
	};

	const resolvePhotoUrl = (path: string) => {
		return getImageUri(path) ?? "";
	};

	const canJumpToStep = (stepIndex: number) => {
		if (!isEditMode) return false;
		return stepIndex >= 0 && stepIndex < steps.length;
	};

	const showAlert = ({
		title,
		message,
		variant = "info",
		confirmText,
	}: AlertPayload) => {
		setAlertState({ open: true, title, message, variant, confirmText });
	};

	const handleCloseAlert = () => {
		setAlertState((prev) => ({ ...prev, open: false }));
		if (shouldCloseOnAlertDismiss) {
			setShouldCloseOnAlertDismiss(false);
			onClose();
		}
	};

	const openConfirm = ({
		title,
		message,
		confirmLabel = "Confirmar",
		cancelLabel = "Cancelar",
		variant = "primary",
		onConfirm,
	}: ConfirmPayload) => {
		setConfirmState({
			open: true,
			title,
			message,
			confirmLabel,
			cancelLabel,
			variant,
			onConfirm,
		});
	};

	const handleCloseConfirm = () => {
		setConfirmState((prev) => ({ ...prev, open: false }));
	};

	const handleSubmitService = async () => {
		if (!onSubmit) {
			setShouldCloseOnAlertDismiss(false);
			showAlert({
				title: "Acción no disponible",
				message: "No se pudo procesar la solicitud en este momento.",
				variant: "error",
			});
			return;
		}

		try {
			await onSubmit({
				categoryId:
					selectedCategoryMeta && selectedCategoryMeta.id > 0
						? selectedCategoryMeta.id
						: undefined,
				...form,
				subcategory: selectedSubcategories,
				includes,
				excludes,
				photos,
			});
			setShouldCloseOnAlertDismiss(true);
			showAlert({
				title: isEditMode ? "Servicio actualizado" : "Nuevo servicio",
				message: isEditMode
					? "Felicidades, se ha actualizado exitosamente el servicio."
					: "Felicidades, se ha registrado exitosamente un nuevo servicio.",
				variant: "success",
			});
		} catch (error) {
			console.error("Error al guardar servicio:", error);
			setShouldCloseOnAlertDismiss(false);
			showAlert({
				title: "No se pudo guardar",
				message:
					error instanceof Error
						? error.message
						: "Ocurrió un problema al procesar el servicio. Inténtalo nuevamente.",
				variant: "error",
			});
		}
	};

	if (!isOpen) return null;

	const handleNext = () => {
		if (currentStep === 1) {
			const errors: Record<string, string> = {};
			if (!form.category) errors.category = "Selecciona una categoría.";
			if (selectedSubcategories.length === 0)
				errors.subcategory = "Selecciona al menos una subcategoría.";
			if (!form.name.trim()) errors.name = "Ingresa el nombre del servicio.";
			else if (form.name.trim().length < 3)
				errors.name = "El nombre debe tener al menos 3 caracteres.";
			if (!form.price) errors.price = "Ingresa el precio base.";
			else if (Number(form.price) <= 0)
				errors.price = "El precio debe ser mayor a 0.";
			if (!form.warrantyDays) errors.warrantyDays = "Ingresa la garantía en días.";
			else if (Number(form.warrantyDays) <= 0)
				errors.warrantyDays = "La garantía debe ser mayor a 0.";
			if (!form.estimatedTimeHours)
				errors.estimatedTimeHours = "Ingresa el tiempo estimado en horas.";
			else if (Number(form.estimatedTimeHours) <= 0)
				errors.estimatedTimeHours = "El tiempo estimado debe ser mayor a 0.";
			if (!form.description.trim())
				errors.description = "Agrega la descripción del servicio.";
			else if (form.description.trim().length < 10)
				errors.description = "La descripción debe tener al menos 10 caracteres.";

			if (Object.keys(errors).length > 0) {
				setFormErrors(errors);
				return;
			}

			setFormErrors({});
		}

		if (currentStep === 2) {
			if (includes.length === 0) {
				setIncludeError("Agrega al menos un ítem que incluya el servicio.");
				return;
			}
			setIncludeError("");
		}

		if (currentStep === 3) {
			const totalPhotos = photos.length + existingPhotos.length;
			if (!isEditMode && totalPhotos === 0) {
				setPhotoError("Debes subir al menos una imagen para crear el servicio.");
				return;
			}
			setPhotoError("");
		}

		if (currentStep < steps.length) {
			setCurrentStep((prev) => prev + 1);
			return;
		}

		openConfirm({
			title: isEditMode ? "Actualizar" : "Guardar",
			message: isEditMode
				? "¿Deseas actualizar este servicio?"
				: "¿Deseas guardar el nuevo servicio?",
			confirmLabel: "Sí",
			cancelLabel: "No",
			onConfirm: handleSubmitService,
		});
	};

	const handlePrev = () => {
		if (currentStep > 1) {
			setCurrentStep((prev) => prev - 1);
		}
	};

	const handleJumpToStep = (stepIndex: number) => {
		if (!canJumpToStep(stepIndex)) return;
		setCurrentStep(stepIndex + 1);
	};

	const confirmDelete = (type: "include" | "exclude", value: string) => {
		openConfirm({
			title: "Eliminar",
			message: "¿Deseas eliminar este ítem?",
			confirmLabel: "Sí",
			cancelLabel: "No",
			variant: "danger",
			onConfirm: () => {
				if (type === "include") {
					setIncludes((prev) => prev.filter((x) => x !== value));
				} else {
					setExcludes((prev) => prev.filter((x) => x !== value));
				}
			},
		});
	};

	return (
		<>
			<div className='fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/50 px-4 py-6'>
				<div className='relative w-full max-w-4xl overflow-hidden rounded-3xl bg-white'>
					<button
						type='button'
						onClick={onClose}
						className='cursor-pointer absolute right-5 top-5 text-slate-500 transition hover:text-slate-700'
						aria-label='Cerrar modal'
					>
						<X className='h-5 w-5' />
					</button>

					<div className='max-h-[90vh] overflow-y-auto overscroll-contain p-8'>
						<header className='mb-4 flex items-start gap-3 pr-8'>
							<div>
								<h2 className='text-xl font-semibold text-slate-900'>
									{isEditMode
										? "Editar Servicio"
										: "Agregar Nuevo Servicio"}
								</h2>
							</div>
						</header>

						<div className='pb-4'>
							<div className='mb-6'>
								<WizardStepper
									step={currentStep - 1}
									total={3}
									onStepClick={
										isEditMode ? handleJumpToStep : undefined
									}
								/>
							</div>

							{currentStep === 1 && (
								<div className='space-y-4 rounded-2xl border border-slate-200 bg-white p-6'>
									<div className='grid gap-4 sm:grid-cols-2'>
										<label className='space-y-1 text-sm text-slate-700'>
											<span className='font-semibold text-slate-900'>
												Categoría *
											</span>
											{availableCategories.length === 0 ? (
												<p className='rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-400'>
													No hay categorías asignadas a tu
													perfil.
												</p>
											) : (
												<select
													value={form.category}
													onChange={(e) => {
														setForm((prev) => ({
															...prev,
															category: e.target.value,
														}));
														setSelectedSubcategories([]);
														setOpenSubcategoryDropdown(false);
														clearError("category");
														clearError("subcategory");
													}}
													className='select-with-arrow w-full cursor-pointer rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
												>
													<option value='' disabled>
														Selecciona la categoría
													</option>
													{availableCategories.map((option) => (
														<option
															key={`${option.id}-${option.label}`}
															value={option.label}
														>
															{option.label}
														</option>
													))}
												</select>
											)}
											{formErrors.category && (
												<p className='text-xs font-semibold text-rose-600'>
													{formErrors.category}
												</p>
											)}
										</label>
										<div className='space-y-1 text-sm text-slate-700'>
											<span className='font-semibold text-slate-900'>
												Subcategoría *
											</span>
											<div className='relative'>
												<button
													type='button'
													disabled={subcategoryDisabled}
													onClick={() =>
														setOpenSubcategoryDropdown(
															(v) => !v,
														)
													}
													className={`flex w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary ${
														subcategoryDisabled
															? "cursor-not-allowed bg-slate-100 text-slate-400"
															: "cursor-pointer text-slate-800"
													}`}
												>
													<span
														className={
															selectedSubcategories.length >
															0
																? "text-slate-700 truncate"
																: "text-slate-400"
														}
													>
														{selectedSubcategoryLabel}
													</span>
													<ChevronDown
														className={`h-5 w-5 text-slate-600 transition-transform ${openSubcategoryDropdown ? "rotate-180" : ""}`}
													/>
												</button>

												{openSubcategoryDropdown &&
													availableSubcategories.length > 0 && (
														<div className='absolute z-20 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-lg'>
															<div className='max-h-48 overflow-auto py-1'>
																{availableSubcategories.map(
																	(sub) => {
																		const selected =
																			selectedSubcategories.includes(
																				sub,
																			);
																		return (
																			<button
																				key={sub}
																				type='button'
																				onClick={() =>
																					selectSubcategory(
																						sub,
																					)
																				}
																				className='flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50'
																			>
																				<span
																					className={`inline-flex h-4 w-4 items-center justify-center rounded border text-xs ${
																						selected
																							? "border-primary bg-primary text-white"
																							: "border-slate-300 bg-white text-transparent"
																					}`}
																				>
																					<Check className='h-3 w-3' />
																				</span>
																				<span>
																					{sub}
																				</span>
																			</button>
																		);
																	},
																)}
															</div>
														</div>
													)}
											</div>
											{formErrors.subcategory && (
												<p className='text-xs font-semibold text-rose-600'>
													{formErrors.subcategory}
												</p>
											)}
										</div>
									</div>
									<div className='grid gap-4 sm:grid-cols-2'>
										<label className='space-y-1 text-sm text-slate-700'>
											<span className='font-semibold text-slate-900'>
												Nombre del Servicio *
											</span>
											<input
												type='text'
												value={form.name}
												onChange={(e) =>
													updateForm("name", e.target.value)
												}
												placeholder='Ej: Instalación de puertas de madera'
												className='w-full rounded-xl border mb-4 border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
											/>
											{formErrors.name && (
												<p className='text-xs font-semibold text-rose-600'>
													{formErrors.name}
												</p>
											)}
										</label>
										<label className='space-y-1 text-sm text-slate-700'>
											<span className='font-semibold text-slate-900'>
												Tiempo Estimado (Horas) *
											</span>
											<input
												type='number'
												min='1'
												value={form.estimatedTimeHours}
												onChange={(e) =>
													updateForm(
														"estimatedTimeHours",
														e.target.value,
													)
												}
												placeholder='8'
												className='w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
											/>
											<p className='text-xs text-slate-500'>
												Tiempo aproximado para completar el
												servicio.
											</p>
											{formErrors.estimatedTimeHours && (
												<p className='text-xs font-semibold text-rose-600'>
													{formErrors.estimatedTimeHours}
												</p>
											)}
										</label>
									</div>

									<div className='grid gap-4 sm:grid-cols-2'>
										<label className='space-y-1 text-sm text-slate-700'>
											<span className='font-semibold text-slate-900'>
												Precio Base *
											</span>
											<div className='relative'>
												<span className='pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400'>
													$
												</span>
												<input
													type='number'
													min='0'
													value={form.price}
													onChange={(e) =>
														updateForm(
															"price",
															e.target.value,
														)
													}
													placeholder='500'
													className='w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 pl-7 text-sm text-slate-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
												/>
											</div>
											<p className='text-xs text-slate-500'>
												Este es el precio inicial, puedes
												ajustarlo en cada cotización.
											</p>
											{formErrors.price && (
												<p className='text-xs font-semibold text-rose-600'>
													{formErrors.price}
												</p>
											)}
										</label>

										<label className='space-y-1 text-sm text-slate-700'>
											<span className='font-semibold text-slate-900'>
												Garantía (Días) *
											</span>
											<input
												type='number'
												min='0'
												value={form.warrantyDays}
												onChange={(e) =>
													updateForm(
														"warrantyDays",
														e.target.value,
													)
												}
												placeholder='30'
												className='w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
											/>
											<p className='text-xs text-slate-500'>
												Tiempo en el que garantizas tu trabajo.
											</p>
											{formErrors.warrantyDays && (
												<p className='text-xs font-semibold text-rose-600'>
													{formErrors.warrantyDays}
												</p>
											)}
										</label>
									</div>

									<label className='space-y-1 text-sm text-slate-700'>
										<span className='font-semibold text-slate-900'>
											Descripción del Servicio *
										</span>
										<textarea
											value={form.description}
											onChange={(e) =>
												updateForm("description", e.target.value)
											}
											rows={4}
											placeholder='Describe detalladamente lo que incluye tu servicio...'
											className='w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
										/>
										{formErrors.description && (
											<p className='text-xs font-semibold text-rose-600'>
												{formErrors.description}
											</p>
										)}
									</label>
								</div>
							)}

							{currentStep === 2 && (
								<div className='space-y-6 rounded-2xl border border-slate-200 bg-white p-6'>
									<div className='space-y-3'>
										<label className='space-y-1 text-sm text-slate-700'>
											<span className='font-semibold text-slate-900'>
												¿Qué Incluye el Servicio? * (mínimo 1)
											</span>
											<div className='flex items-center gap-3'>
												<input
													type='text'
													value={includeInput}
													onChange={(e) =>
														setIncludeInput(e.target.value)
													}
													placeholder='Ej: Materiales de instalación'
													className='h-11 flex-1 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
												/>
												<button
													type='button'
													onClick={() => {
														const value = includeInput.trim();
														if (!value) return;
														if (includes.includes(value)) {
															setIncludeInput("");
															return;
														}
														setIncludes((prev) => [
															...prev,
															value,
														]);
														setIncludeInput("");
														if (includeError)
															setIncludeError("");
													}}
													className='flex h-11 w-11 min-w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-sm transition hover:bg-primary-hover cursor-pointer'
													aria-label='Agregar ítem incluido'
												>
													<Plus className='h-5 w-5' />
												</button>
											</div>
										</label>
										{includes.length > 0 && (
											<ul className='space-y-3 mt-2'>
												{includes.map((item) => (
													<li
														key={item}
														className='flex items-center gap-3 rounded-xl bg-secondary px-3 py-2.5 text-sm text-slate-800'
													>
														<span className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary'>
															<Check className='h-4 w-4' />
														</span>
														<span className='flex-1'>
															{item}
														</span>
														<button
															type='button'
															onClick={() =>
																confirmDelete(
																	"include",
																	item,
																)
															}
															className='cursor-pointer text-rose-500 transition hover:text-rose-600'
															aria-label='Eliminar ítem incluido'
														>
															<Trash2 className='h-4 w-4' />
														</button>
													</li>
												))}
											</ul>
										)}
										{includeError && (
											<p className='text-xs font-semibold text-rose-600'>
												{includeError}
											</p>
										)}
									</div>

									<div className='space-y-3'>
										<label className='space-y-1 text-sm text-slate-700'>
											<span className='font-semibold text-slate-900'>
												¿Qué NO Incluye el Servicio?{" "}
												<span className='text-option-muted'>
													(Opcional)
												</span>
											</span>
											<div className='flex items-center gap-3'>
												<input
													type='text'
													value={excludeInput}
													onChange={(e) =>
														setExcludeInput(e.target.value)
													}
													placeholder='Ej: Pintura de acabado'
													className='h-11 flex-1 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
												/>
												<button
													type='button'
													onClick={() => {
														const value = excludeInput.trim();
														if (!value) return;
														if (excludes.includes(value)) {
															setExcludeInput("");
															return;
														}
														setExcludes((prev) => [
															...prev,
															value,
														]);
														setExcludeInput("");
													}}
													className='flex h-11 w-11 min-w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-sm transition hover:bg-primary-hover cursor-pointer'
													aria-label='Agregar ítem no incluido'
												>
													<Plus className='h-5 w-5' />
												</button>
											</div>
										</label>
										{excludes.length > 0 && (
											<ul className='space-y-3 mt-2'>
												{excludes.map((item) => (
													<li
														key={item}
														className='flex items-center gap-3 rounded-xl bg-slate-100 px-3 py-2.5 text-sm text-slate-800'
													>
														<span className='flex-1'>
															{item}
														</span>
														<button
															type='button'
															onClick={() =>
																confirmDelete(
																	"exclude",
																	item,
																)
															}
															className='cursor-pointer text-rose-500 transition hover:text-rose-600'
															aria-label='Eliminar ítem no incluido'
														>
															<Trash2 className='h-4 w-4' />
														</button>
													</li>
												))}
											</ul>
										)}
									</div>
								</div>
							)}

							{currentStep === 3 && (
								<div className='space-y-4 rounded-2xl border border-slate-200 bg-white p-6'>
									<p className='text-sm font-semibold text-slate-900'>
										Fotos del Servicio
									</p>
									<p className='text-xs text-slate-500'>
										{isEditMode
											? "Puedes conservar las fotos actuales o agregar nuevas."
											: "Debes cargar al menos una foto para publicar tu servicio."}
									</p>

									{existingPhotos.length > 0 && (
										<div className='space-y-2'>
											<p className='text-xs font-semibold uppercase tracking-wide text-slate-400'>
												Actuales
											</p>
											<div className='grid grid-cols-2 gap-3 sm:grid-cols-3'>
												{existingPhotos.map((photoPath, idx) => {
													const src =
														resolvePhotoUrl(photoPath);
													if (!src) return null;
													return (
														<div
															key={`${photoPath}-${idx}`}
															className='overflow-hidden rounded-xl border border-slate-200 bg-slate-100'
														>
															<img
																src={src}
																alt={`Foto actual ${idx + 1}`}
																className='h-24 w-full object-cover'
															/>
														</div>
													);
												})}
											</div>
										</div>
									)}

									<label className='block cursor-pointer'>
										<input
											type='file'
											accept='image/png,image/jpeg'
											multiple
											className='hidden'
											onChange={(e) => {
												const files = Array.from(
													e.target.files ?? [],
												);
												if (files.length === 0) return;
												const filtered = files.filter(
													(file) =>
														file.size <= 5 * 1024 * 1024,
												);
												if (filtered.length > 0 && photoError) {
													setPhotoError("");
												}
												setPhotos((prev) => [
													...prev,
													...filtered,
												]);
												e.target.value = "";
											}}
										/>

										<div className='flex min-h-44 w-full flex-col items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-6 py-10 text-center'>
											<div className='mb-3 text-slate-400'>
												<Upload className='h-10 w-10' />
											</div>
											<p className='text-sm font-semibold text-slate-700'>
												Haz clic para subir fotos
											</p>
											<p className='mt-1 text-xs text-slate-500'>
												PNG ,JPG hasta 5MB cada una
											</p>
										</div>
									</label>

									{photos.length > 0 && (
										<div className='space-y-2'>
											<p className='text-xs font-semibold uppercase tracking-wide text-slate-400'>
												Seleccionadas
											</p>
											<div className='grid grid-cols-2 gap-3 sm:grid-cols-3'>
												{photos.map((file, idx) => (
													<div
														key={`${file.name}-${file.size}-${idx}`}
														className='overflow-hidden rounded-xl border border-slate-200 bg-slate-100'
													>
														{photoPreviewUrls[idx] ? (
															<img
																src={
																	photoPreviewUrls[idx]
																}
																alt={file.name}
																className='h-24 w-full object-cover'
															/>
														) : (
															<div className='flex h-24 w-full items-center justify-center text-xs text-slate-500'>
																Sin preview
															</div>
														)}
														<div className='flex items-center gap-2 px-2 py-1'>
															<span className='flex-1 truncate text-xs text-slate-700'>
																{file.name}
															</span>
															<button
																type='button'
																onClick={() =>
																	setPhotos((prev) =>
																		prev.filter(
																			(_, i) =>
																				i !== idx,
																		),
																	)
																}
																className='cursor-pointer text-rose-500 transition hover:text-rose-600'
																aria-label='Eliminar foto'
															>
																<Trash2 className='h-4 w-4' />
															</button>
														</div>
													</div>
												))}
											</div>
										</div>
									)}

									{photoError ? (
										<p className='text-xs font-semibold text-rose-600'>
											{photoError}
										</p>
									) : null}
								</div>
							)}
						</div>

						<footer className='mt-4 flex items-center justify-between border-t border-slate-200 pt-4'>
							{currentStep === 1 ? (
								<div />
							) : (
								<button
									type='button'
									onClick={handlePrev}
									className='cursor-pointer rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-primary hover:text-primary'
								>
									Anterior
								</button>
							)}
							<button
								type='button'
								onClick={handleNext}
								className='cursor-pointer rounded-full bg-primary px-6 py-2 text-sm font-semibold text-white transition hover:bg-primary-hover'
							>
								{currentStep === steps.length
									? isEditMode
										? "Actualizar"
										: "Guardar"
									: "Siguiente"}
							</button>
						</footer>
					</div>
				</div>
			</div>

			<AlertModal
				open={alertState.open}
				onClose={handleCloseAlert}
				title={alertState.title}
				message={alertState.message}
				variant={alertState.variant}
				confirmText={alertState.confirmText}
			/>

			<ConfirmModal
				open={confirmState.open}
				title={confirmState.title}
				message={confirmState.message}
				confirmLabel={confirmState.confirmLabel}
				cancelLabel={confirmState.cancelLabel}
				variant={confirmState.variant}
				onCancel={handleCloseConfirm}
				onConfirm={async () => {
					try {
						await confirmState.onConfirm();
					} finally {
						handleCloseConfirm();
					}
				}}
			/>
		</>
	);
}
